/**
 * Finance Essentials Reporting Pack — client.
 *
 * Single endpoint: POST /api/inventory/reports/export.
 * Returns a binary file (xlsx or pdf). This module owns the request +
 * blob-to-download wiring so callers stay declarative.
 */

import AxiosInstance from '../config/AxiosInstance';

export type ReportType =
  | 'sales-summary'
  | 'tender-summary'
  | 'tax-summary'
  | 'refund-summary'
  | 'void-summary'
  | 'cashup'
  | 'transaction-detail'
  | 'sales-by-product-group'
  | 'sales-by-product';

export type ExportFormat = 'xlsx' | 'pdf';

export interface ExportReportPayload {
  reportType: ReportType;
  format: ExportFormat;
  /** ISO-8601 with timezone designator (Z or ±HH:MM). */
  dateFrom: string;
  /** ISO-8601 with timezone designator (Z or ±HH:MM). */
  dateTo: string;
  /**
   * Optional dd/mm/yyyy label echoed into the export's metadata block.
   * Lets the user see exactly the dates they picked (in their local
   * format) — independent of the server's timezone interpretation of the
   * ISO timestamps above. When omitted the backend falls back to UTC
   * dd/mm/yyyy.
   */
  dateFromLabel?: string;
  dateToLabel?: string;
  /** Null / undefined = all locations the caller's company can see. */
  locationId?: string | null;
  /**
   * Locale for server-rendered labels inside the XLSX / PDF export
   * (metadata block, column headers, totals row). The on-screen UI
   * translates separately. Backend defaults to "en" when omitted.
   */
  locale?: string;
  /**
   * IANA zone of the browser (Intl.DateTimeFormat().resolvedOptions().timeZone).
   * The server renders Transaction Detail "Date" / Cash-up "Closed At" as
   * wall-clock time in it and counts the window's calendar days in it.
   * Defaults to UTC on the backend when omitted.
   */
  tz?: string;
}

export interface ExportReportResult {
  filename: string;
  /** True when the report hit its line cap (X-Report-Truncated header). */
  truncated: boolean;
}

const MIME_TO_EXT: Record<string, ExportFormat> = {
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'xlsx',
  'application/pdf': 'pdf',
};

/**
 * Parse a server-suggested filename out of a Content-Disposition header.
 * Honours the RFC 5987 filename* parameter (UTF-8) when present, falling
 * back to the ASCII filename=. Returns null when neither is parseable.
 */
function filenameFromContentDisposition(header: string | undefined): string | null {
  if (!header) return null;
  // filename*=UTF-8''<percent-encoded>  (preferred)
  const star = /filename\*\s*=\s*UTF-8''([^;]+)/i.exec(header);
  if (star) {
    try {
      return decodeURIComponent(star[1].trim());
    } catch {
      // fall through to the ASCII variant
    }
  }
  // filename="..."  or  filename=...
  const plain = /filename\s*=\s*"?([^";]+)"?/i.exec(header);
  return plain ? plain[1].trim() : null;
}

/**
 * Trigger a browser download for a Blob. Builds a hidden <a download> that
 * gets click-dispatched then revoked. Safe in modern browsers (Chromium,
 * Firefox, Safari).
 */
function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  try {
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.style.display = 'none';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  } finally {
    // Revoke on the next tick — some browsers race the click handler with
    // synchronous revocation and end up cancelling the download.
    setTimeout(() => URL.revokeObjectURL(url), 0);
  }
}

/**
 * The request is made with responseType 'blob', so an API error body arrives
 * as a Blob too and `error.response.data.message` is undefined. Read the
 * blob back as text and, when it is the API's JSON envelope, put the parsed
 * body where callers (and apiError.describeApiError) expect it.
 */
async function unwrapBlobError(error: any): Promise<any> {
  const data = error?.response?.data;
  if (typeof Blob !== 'undefined' && data instanceof Blob) {
    try {
      const text = await data.text();
      try {
        error.response.data = JSON.parse(text);
      } catch {
        error.response.data = { message: text };
      }
    } catch {
      // unreadable blob: leave the error as it is
    }
  }
  return error;
}

/**
 * Request a finance report from the API and trigger a browser download.
 *
 * @returns the resolved filename (for a confirmation toast) and whether the
 * server truncated the report at its line cap.
 */
const exportReport = async (payload: ExportReportPayload): Promise<ExportReportResult> => {
  let response;
  try {
    response = await AxiosInstance.post(
      '/api/inventory/reports/export',
      payload,
      { responseType: 'blob' },
    );
  } catch (error) {
    throw await unwrapBlobError(error);
  }

  const contentType: string =
    (response.headers && (response.headers['content-type'] as string)) ||
    'application/octet-stream';
  const contentDisposition: string | undefined =
    response.headers && (response.headers['content-disposition'] as string);

  const ext = MIME_TO_EXT[contentType.split(';')[0].trim()] ?? payload.format;
  const fallbackName = `${payload.reportType}.${ext}`;
  const filename = filenameFromContentDisposition(contentDisposition) ?? fallbackName;

  const blob = new Blob([response.data as BlobPart], { type: contentType });
  downloadBlob(blob, filename);

  const truncated =
    String(response.headers?.['x-report-truncated'] ?? '').toLowerCase() === 'true';
  return { filename, truncated };
};

export default {
  exportReport,
};
